import axios from "axios";
import baseURL from "config";
import ApiService, { errorMessage } from "helpers/ApiHelper";

const ROOT = "/api/v1/pages";

/**
 * Page keys are paths, not path segments — `home`, but also
 * `parents/online-consultation-with-a-vet`. The API declares every keyed route
 * with the key as a trailing wildcard for exactly that reason, so the key goes
 * on the URL raw. Do not `encodeURIComponent` it: `%2F` collapses the key into
 * one segment that no route matches, and it only survives the trip if every
 * proxy in front (Caddy locally, whatever fronts production) leaves it
 * un-normalised.
 */
const keyed = (prefix, pageKey) => `${ROOT}/${prefix}/${pageKey}`;


/** List of editable pages — structure only, no fields, no values. */
export const fetchManifests = () => ApiService.request(`${ROOT}/manifests`, "get");

/**
 * The published snapshot for one language, keyed by page. Used by the list to
 * mark which pages are live; it is the same public read the Astro build makes,
 * so one request covers all nine pages.
 */
export const fetchPublishedPages = (lang) => ApiService.request(`${ROOT}/public/${lang}`, "get");

/** Manifest plus the stored draft, in one round trip. `page` is null when nothing has been edited yet. */
export const fetchPageForEditor = (pageKey, lang) =>
  ApiService.request(keyed(`edit/${lang}`, pageKey), "get");

/**
 * Saves the whole field map, never a patch — the form always holds every
 * field, and a merge would make "clear this field" indistinguishable from
 * "this field was not in the payload". A save only writes the draft; it never
 * changes what the site serves.
 */
export const savePageDraft = (pageKey, lang, fields) =>
  ApiService.request(keyed(`edit/${lang}`, pageKey), "put", { fields });

/** Publish copies the draft into the served snapshot; unpublish empties it. Neither touches the draft. */
export const setPageStatus = (pageKey, lang, status) =>
  ApiService.request(keyed(`status/${lang}`, pageKey), "patch", { status });

/**
 * Live draft preview (DAB-861). The GET mints nothing: it answers 503
 * `preview_unavailable` in environments without the preview service, and the
 * editor hides the feature. The POST mints a short-lived session URL on the
 * preview host for this page and language.
 */
export const fetchLivePreviewAvailability = () =>
  ApiService.request(`${ROOT}/preview-session`, "get");

export const requestLivePreviewSession = (pageKey, lang) =>
  ApiService.request(`${ROOT}/preview-session`, "post", { pageKey, lang });

/**
 * Shared upload endpoint — returns the public URL of the stored object.
 *
 * Not routed through `ApiService`: this endpoint answers `{imageUrl}` rather
 * than the `{status, data}` envelope the helper unwraps, and it needs the
 * multipart content type set explicitly, as the other four upload call sites
 * in this app do.
 */
export const uploadImage = async (file) => {
  const body = new FormData();
  body.append("file", file);
  const response = await axios.post(`${baseURL}/api/v1/upload`, body, {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data?.imageUrl;
};

/**
 * The featured-professionals picker (DAB-852). `q` searches professionals the
 * homepage row would actually show — by name, email or a pasted id.
 */
export const searchProfessionals = (q) =>
  ApiService.request(`${ROOT}/professional-picker?q=${encodeURIComponent(q)}`, "get");

/** Chosen ids -> name, role, photo and whether the site still shows them, in order. */
export const fetchProfessionalsByIds = (ids) =>
  ApiService.request(`${ROOT}/professional-picker?ids=${ids.map(encodeURIComponent).join(",")}`, "get");

// Re-exported so the screens keep importing it from their own api module.
export { errorMessage };
